/**
 * useInteriorVariant — Reactive interior variant hook.
 *
 * Syncs the 3D model's interior materials with the active pano variant.
 * Each pano variant ID maps to an `interiorVariants[]` entry in model config
 * which defines per-material colour overrides and diffuse atlas texture swaps.
 *
 * Lifecycle:
 *   1. On sceneLoaded: apply the default (first) interior variant immediately
 *      (no animation — the car appears pre-dressed when the scene fades in).
 *   2. On pano variant change: apply the matching interior variant in the
 *      background. Since the user is looking at the pano sphere, no visual
 *      glitch is visible even if texture loading takes a moment.
 *
 * Texture management:
 *   - Textures are loaded on-demand via engine.setMaterial({ textures }).
 *   - The engine's AssetLoader caches by URL, so revisiting a variant does
 *     not re-download or duplicate GPU memory.
 *   - MaterialManager.dispose() cleans up all atlas clones on engine teardown.
 *   - Only the default variant's textures are loaded eagerly (on scene load).
 *     Subsequent variant textures load lazily when the user switches pano.
 *
 * Call site: Viewer.tsx  (has access to both engineRef and config)
 *
 * @see packages/engine/src/core/MaterialManager.ts — atlas track
 * @see docs/ims-engine-api/06-feature-color-system.md
 */
import { useEffect, useRef } from 'react';
import type { Engine } from '@ims/engine';
import type { ResolvedConfig, InteriorVariant } from '@ims/shared';
import { useAppStore } from '../store/app.store';
import { isMobilePhone } from '../utils/isMobilePhone';

export function useInteriorVariant(
  engineRef: React.RefObject<Engine | null>,
  config: ResolvedConfig | null,
): void {
  const selectedPanoId = useAppStore((s) => s.selectedPanoId);
  const sceneReady = useAppStore((s) => s.sceneReady);

  // Track which variant is currently applied so we don't re-apply on no-ops.
  const appliedVariantRef = useRef<string | null>(null);

  // Resolve variant helpers from config
  const interiorVariants = config?.model.interiorVariants;
  const panoVariants = config?.panoVariants;

  // ── Initial apply (on sceneReady) ──────────────────────────────────────
  useEffect(() => {
    if (!sceneReady || !interiorVariants?.length || !panoVariants?.length) return;
    const engine = engineRef.current;
    if (!engine) return;

    // Default = first variant
    const defaultVariant = interiorVariants[0];
    applyVariant(engine, defaultVariant);
    appliedVariantRef.current = defaultVariant.id;
  }, [sceneReady, engineRef, interiorVariants, panoVariants]);

  // ── Reactive apply (on pano variant change) ────────────────────────────
  useEffect(() => {
    if (!sceneReady || !interiorVariants?.length || !panoVariants?.length) return;
    if (!selectedPanoId) return; // null = default, handled by initial apply

    // Skip if already applied
    if (selectedPanoId === appliedVariantRef.current) return;

    const engine = engineRef.current;
    if (!engine) return;

    const variant = interiorVariants.find((v) => v.id === selectedPanoId);
    if (!variant) return; // No interior variant for this pano — leave as-is

    applyVariant(engine, variant);
    appliedVariantRef.current = selectedPanoId;
  }, [selectedPanoId, sceneReady, engineRef, interiorVariants, panoVariants]);
}

/**
 * Apply an interior variant's material changes to the engine.
 * Each material change may include a colour override and/or a diffuse texture swap.
 */
function applyVariant(engine: Engine, variant: InteriorVariant): void {
  for (const mat of variant.materials) {
    const opts: Parameters<Engine['setMaterial']>[1] = {};

    if (mat.colorCode) {
      opts.colorCode = mat.colorCode;
      opts.animated = false; // instant — user sees pano, not the 3D model
    }

    if (mat.diffuseUrl) {
      const url = isMobilePhone() && mat.mobileDiffuseUrl ? mat.mobileDiffuseUrl : mat.diffuseUrl;
      opts.textures = { diffuseUrl: url };
    }

    // Only call setMaterial if there's something to apply
    if (opts.colorCode || opts.textures) {
      const targets = Array.isArray(mat.target) ? mat.target : [mat.target];
      for (const t of targets) {
        engine.setMaterial(t, opts);
      }
    }
  }
}
